import { useEffect, useMemo, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { announce, islandListening } from '@/features/island/islandBus'
import { focusAllowsKind, useFocusMode } from '@/features/focus'
import { apiGet, apiPost } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { cardsApi } from '@/features/cards/api'
import type { Card, Member } from '@/types/entities'
import {
  shouldShowWebsiteNotification,
  useWebsiteNotificationPreferences,
} from './websiteNotificationPreferences'

/**
 * The bell's data, and the popup that announces a new one.
 *
 * Polled, because this app has no realtime and the board already polls — adding a socket for
 * a bell would be the first one in the product, and the first one is never just one.
 *
 * Ten seconds. Event writes are immediate; polling is only how the open browser discovers
 * the new row. This keeps approval/status changes feeling live without adding a socket solely
 * for the bell.
 */
const POLL_MS = 10_000

export interface AppNotification {
  id: string
  kind: string
  title: string
  body: string | null
  actorId: string | null
  cardId: string | null
  href: string | null
  readAt: string | null
  createdAt: string
}

interface Inbox {
  items: AppNotification[]
  unread: number
}

function needsWorkContext(notification: AppNotification): boolean {
  if (!notification.cardId || !notification.href?.startsWith('/work')) return false
  return (
    notification.kind === 'new_work' ||
    notification.kind === 'status_changed' ||
    notification.kind === 'completed' ||
    notification.kind === 'blocked' ||
    notification.kind === 'sent_back'
  )
}

export function useNotifications(workspaceSlug: string) {
  const queryClient = useQueryClient()
  const { userId } = useIdentity()
  const { preferences } = useWebsiteNotificationPreferences(workspaceSlug)

  const query = useQuery({
    queryKey: qk.notifications(),
    queryFn: () => apiGet<Inbox>('notify/inbox', { limit: 30 }),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    /*
     * The one query in the app that DOES refetch on focus.
     *
     * The global default is off, because a board that reshuffles under the cursor is worse
     * than a stale board. A bell has no cursor over it and the whole point is to be current
     * the moment somebody looks — coming back to the tab is exactly when they do.
     */
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  })

  const rawItems = query.data?.items ?? []
  const contextCardIds = useMemo(
    () => [...new Set(rawItems.filter(needsWorkContext).map((item) => item.cardId!).filter(Boolean))],
    [rawItems],
  )
  const contextKey = contextCardIds.join('|')

  // One batched read supplies the relationship context needed by the two team/status switches.
  // It is read-only and skipped entirely when the current inbox has no relevant Work Items.
  const workContext = useQuery({
    queryKey: ['notify', 'website-work-context', contextKey],
    queryFn: () => cardsApi.query({ filter: { ids: contextCardIds }, limit: Math.min(200, Math.max(1, contextCardIds.length)) }),
    enabled: contextCardIds.length > 0 && preferences.notifyWebsite,
    staleTime: 60_000,
    retry: false,
  })

  const members = useQuery({
    queryKey: qk.members(),
    queryFn: () => apiGet<{ items: Member[] }>('members'),
    enabled: contextCardIds.length > 0 && preferences.notifyWebsite,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const cardsById = useMemo(
    () =>
      new Map<string, Card>(
        (workContext.data?.items ?? []).map((card): [string, Card] => [card.id, card]),
      ),
    [workContext.data],
  )
  const me = useMemo(
    () => (members.data?.items ?? []).find((member) => member.id === userId) ?? null,
    [members.data, userId],
  )

  const items = useMemo(
    () => rawItems.filter((notification) =>
      shouldShowWebsiteNotification(
        notification,
        preferences,
        notification.cardId ? cardsById.get(notification.cardId) : null,
        me,
      )),
    [cardsById, me, preferences, rawItems],
  )

  // The server's unread total includes rows this browser may intentionally suppress. Count the
  // visible page instead so muted website categories do not leave a permanent red badge.
  const unread = items.filter((notification) => !notification.readAt).length

  /*
   * A toast for anything that arrived while they were here.
   *
   * Only for genuinely NEW visible rows, which is why the seen set is seeded on the first
   * successful filtered result rather than starting empty: an empty one would announce the
   * entire backlog as news the moment somebody opened the app.
   */
  const seen = useRef<Set<string> | null>(null)
  const { focus } = useFocusMode()
  const focusRef = useRef(focus)
  useEffect(() => {
    focusRef.current = focus
  })
  useEffect(() => {
    if (!query.data?.items) return
    if (seen.current === null) {
      seen.current = new Set(items.map((n) => n.id))
      return
    }
    for (const n of items.filter((n) => !n.readAt && !seen.current!.has(n.id)).reverse()) {
      seen.current.add(n.id)
      // Focus mode: muted categories wait quietly in the bell; the L3 watch always gets through.
      if (!focusAllowsKind(focusRef.current, n.kind)) continue
      if (islandListening()) {
        announce({ id: n.id, title: n.title, body: n.body, href: n.href, tone: n.kind === 'support_l3_watch' ? 'danger' : 'info' })
        continue
      }
      toast(n.title, {
        description: n.body ?? undefined,
        action: n.href
          ? { label: 'Open', onClick: () => { window.location.href = `/w/${workspaceSlug}${n.href}` } }
          : undefined,
      })
    }
    for (const n of items) seen.current.add(n.id)
  }, [items, query.data?.items, workspaceSlug])

  const markRead = useMutation({
    // An empty list means all of them; the server reads it that way.
    mutationFn: (ids: string[] = []) => apiPost<{ marked: number }>('notify/inbox/read', { ids }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.notifications() }),
  })

  return {
    items,
    unread,
    isPending: query.isPending,
    markRead,
  }
}
